import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container } from "reactstrap";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import useStoreFilters from "customHooks/useStoreFilters";
import { FILTER_PAGE, USER_ROLES } from "utils/constants";
import { quarantineMetersListColumns } from "./QuarantineMetersListColumns";
import QuarantineMetersSearch from "./QuarantineMetersSearch";
import { removeFromQuarantine, retrieveMeters } from "utils/api";
import { isEmpty } from "lodash";
import CustomButton from "components/Common/buttons/CustomButton";
import openLock from "assets/images/svg/Open Lock.svg";
import NGITooltip from "components/Common/utils/NGITooltip";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import BulkRemoveMeterModal from "pages/meter-management/quarantineMeters/removeModal";
import { userHasOneOfRoles } from "utils/helpers/functions";
import wrenchIcon from "assets/images/svg/Wrench.svg";

const QuarantineMetersList = () => {
  const [isLoading, setLoader] = useState(false);
  const [list, setList] = useState(null);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.QUARANTINE_METERS);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [removeId, setRemoveId] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isBulkRemoveOpen, setIsBulkRemoveOpen] = useState(false);

  useEffect(() => {
    !isEmpty(filters) ? handleSearch(filters) : handleSearch();
  }, []);

  const onListLoaded = useCallback(res => {
    const {
      result: { items },
    } = res?.data;
    if (items?.length === 0) {
      warnMessage("Quarantine meters were not found");
    }
    setList(res?.data || null);
    setLoader(false);
  }, []);

  const handleSearch = useCallback((filterOptions = {}) => {
    setLoader(true);
    const filters = { ...filterOptions };
    setFilters(filters);
    retrieveMeters({ ...filters, condition: "Quarantine" })
      .then(res => onListLoaded(res))
      .catch(err => {
        setLoader(false);
        setList(null);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const onRemove = useCallback((ids = []) => {
    closeConfirmationModal();
    setLoader(true);
    removeFromQuarantine(ids)
      .then(() => {
        successMessage("Selected meter(s) removed from quarantine successfully");
        handleSearch();
      })
      .catch(err => {
        setLoader(false);
        setList(null);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const rowSelection = {
    setSelectedRowKeys,
    onChange: rows => setSelectedRowKeys(rows),
    renderCell: (checked, record, index, node) => {
      if (selectedRowKeys.length === 10) {
        return <NGITooltip title="Only 10 items are allowed.">{node}</NGITooltip>;
      }
      return node;
    },
    hideSelectAll: true,
    getCheckboxProps: record => {
      const checkboxProps = {};
      if (selectedRowKeys.length === 10 && !selectedRowKeys.includes(record.id)) {
        checkboxProps.disabled = true;
      }
      return checkboxProps;
    },
  };

  const closeBulkRemove = useCallback(() => setIsBulkRemoveOpen(false), []);
  const openBulkRemove = useCallback(() => setIsBulkRemoveOpen(true), []);

  const openConfirmationModal = useCallback(id => {
    setRemoveId(id);
    setIsConfirmationOpen(true);
  }, []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const columns = useMemo(() => quarantineMetersListColumns(openConfirmationModal), []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="filters-wrapper">
            <div className="filter-buttons">
              <CustomButton
                iconImg={wrenchIcon}
                roles={[USER_ROLES.PRODUCT_TECHNICIAN_LEAD]}
                onClick={openBulkRemove}
                type="primary"
                text="BULK REMOVE"
              />
              {!!selectedRowKeys.length && (
                <CustomButton
                  roles={[USER_ROLES.PRODUCT_TECHNICIAN_LEAD]}
                  onClick={() => openConfirmationModal(selectedRowKeys)}
                  isButtonRound
                  iconImg={openLock}
                  type="primary"
                />
              )}
            </div>

            <QuarantineMetersSearch handleSearch={handleSearch} />
          </div>
          <Col lg={12}>
            <NGITable
              filtersType={FILTER_PAGE.QUARANTINE_METERS}
              isLoading={isLoading}
              rowSelection={
                userHasOneOfRoles([USER_ROLES.SYSTEM_FULL_ADMINS, USER_ROLES.PRODUCT_TECHNICIAN_LEAD])
                  ? rowSelection
                  : null
              }
              rowKey={record => record.id}
              columns={columns}
              data={list}
              updateList={handleSearch}
            />
          </Col>

          {isConfirmationOpen && (
            <ConfirmationModal
              description={"Are you sure you want to remove the selected meter(s) from quarantine?"}
              onCancel={closeConfirmationModal}
              onConfirm={() => {
                onRemove(removeId);
              }}
            />
          )}
          {isBulkRemoveOpen && <BulkRemoveMeterModal onClose={closeBulkRemove} />}
        </Container>
      </div>
    </>
  );
};

export default QuarantineMetersList;
